import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  useOrgsAndUserSearch,
  useUpdatePermissionRole,
  useUpdatePermissions,
} from "../mediaAccessServices";
import { useRoles } from "../../user-admin/roleServices";
import { isBlank, notBlank } from "../../util/ext";
import {
  __,
  add,
  always,
  append,
  equals,
  find,
  gt,
  has,
  head,
  identity,
  ifElse,
  includes,
  lensProp,
  lte,
  mergeRight,
  map,
  not,
  pick,
  pickBy,
  pipe,
  reject,
  reduce,
  set,
  type,
  values,
  uniq,
  view,
  without,
  subtract,
} from "ramda";
import { useDebounce } from "../../util/hooks";
import { useSearchFilter } from "../../common/use-searchFilter";
import useInfiniteScroll from "react-infinite-scroll-hook";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import LoadingIndicator from "../../components/LoadingIndicator";
import { FormLabel } from "@mui/material";

const initialSearchState = {
  page: 0,
  appliedFilter: "",
};

const LIMIT = 50;

const permissionsMap = [
  { label: "View", name: "VIEW", value: 1 },
  { label: "Update", name: "UPDATE", value: 2 },
  { label: "Organize", name: "ORGANIZE", value: 4 },
  { label: "Delete", name: "DELETE", value: 16 },
];

const findPermissionName = p => find(pm => equals(pm.name, p), permissionsMap);
const getPermissionValue = map(p =>
  view(lensProp("value"), findPermissionName(p)),
);
const getPermissionValues = pipe(getPermissionValue, reject(isBlank));

const getMethod = (prev, selected) => {
  if (notBlank(prev)) {
    if (lte(selected, prev)) {
      return "DELETE";
    } else {
      return "PUT";
    }
  } else {
    return "POST";
  }
};

const useStyles = makeStyles(theme => ({
  formLabel: {
    fontSize: "1.4rem",
    "&.Mui-focused": {
      color: "rgba(0, 0, 0, 0.54)",
    },
  },
}));

const ManageMediaAccessDialog = ({
  mediaAccess,
  mediaId,
  open,
  onClose,
  showAlert,
}) => {
  const classes = useStyles();
  const isEdit = notBlank(mediaAccess.mediaPermissionsId);

  //-- Manage Search --//
  const { state, handleUpdateFilter, handleUpdatePage } = useSearchFilter(
    initialSearchState,
  );
  const debouncedFilter = useDebounce(state.appliedFilter, 300);

  const rolesResults = useRoles();
  const results = useOrgsAndUserSearch({
    filter: debouncedFilter,
    offset: state.page * LIMIT,
  });

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: results.isLoading,
    hasNextPage: results.hasMore,
    onLoadMore: () => handleUpdatePage(state.page + 1),
    rootMargin: "0px 0px 400px 0px",
    delayInMs: 500,
  });

  const renderSentry = () => {
    return results.hasMore ? (
      <div ref={sentryRef} key="sentry">
        Loading...
      </div>
    ) : null;
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ principal: false });
  const [principal, setPrincipal] = useState("");
  const [principalInput, setPrincipalInput] = useState("");
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  useEffect(() => {
    if (notBlank(mediaAccess.mediaPermissionsId)) {
      if (notBlank(mediaAccess.principal)) {
        setPrincipal(mediaAccess.principal);
      }

      if (notBlank(mediaAccess.role)) {
        setRoles({ roleName: mediaAccess.role });
      }

      if (notBlank(mediaAccess.permissionList)) {
        const permissions = getPermissionValues(mediaAccess.permissionList);
        setPermissions(permissions);
      }
    }
  }, [mediaAccess]);

  const reset = useCallback(() => {
    setPrincipal("");
    setError({ principal: false });
    setRoles([]);
    setPermissions([]);
    setLoading(false);
    handleUpdateFilter("");
  }, [handleUpdateFilter]);

  const updatePermissions = useUpdatePermissions();
  const updatePermissionRole = useUpdatePermissionRole();
  const handleSave = useCallback(async () => {
    try {
      setLoading(true);
      if (isBlank(principal)) {
        setError({ principal: true });
        throw new Error("Please add an organization or user");
      }

      const selectedPermissions = reduce(add, 0, permissions);
      const previousPermissions = mediaAccess.permissions;

      if (
        equals(selectedPermissions, previousPermissions) &&
        equals(mediaAccess.role, roles.roleName)
      ) {
        reset();
        onClose();
        return;
      }

      //-- If updating or creating permission,
      //-- send the selected permissions as is.
      //-- If removing permissions,
      //-- subtract the selected permissions from the previous permissions
      const updatedPermissions = ifElse(
        gt(__, 0),
        identity,
        always(selectedPermissions),
      )(subtract(previousPermissions, selectedPermissions));

      const method = getMethod(previousPermissions, selectedPermissions);

      const accessControl = pickBy(notBlank, {
        mediaPermissionsId: mediaAccess.mediaPermissionsId,
        principal,
        permissions: updatedPermissions,
      });

      if (!isEdit) {
        if (notBlank(roles)) {
          await Promise.all(
            map(role => {
              updatePermissions.mutateAsync({
                id: mediaId,
                accessControl: mergeRight(accessControl, {
                  role: role.roleName,
                }),
                method: method,
              });
            }, roles),
          );
        }
      } else {
        // Update Role
        if (mediaAccess.role !== roles.roleName) {
          await updatePermissionRole.mutateAsync({
            resourceIds: {
              id: mediaId,
            },
            data: {
              mediaPermissionsId: mediaAccess.mediaPermissionsId,
              principal,
              role: roles.roleName,
            },
          });
        }

        // Update Permissions
        await updatePermissions.mutateAsync({
          id: mediaId,
          accessControl: mergeRight(accessControl, {
            role:
              mediaAccess.role !== roles.roleName
                ? roles.roleName
                : mediaAccess.role,
          }),
          method: method,
        });
      }

      const msg = isBlank(mediaAccess.mediaPermissionsId)
        ? "New Permission Added"
        : "Permission Updated";
      showAlert("success", [msg]);

      reset();
      onClose();
    } catch (err) {
      showAlert("error", [err.message]);
    } finally {
      setLoading(false);
    }
  }, [
    isEdit,
    mediaId,
    mediaAccess,
    onClose,
    principal,
    permissions,
    roles,
    reset,
    showAlert,
    updatePermissionRole,
    updatePermissions,
  ]);

  //-- Manage Principal --//
  const handlePrincipalChange = (_event, value) => {
    if (value) {
      const principal = head(values(pick(["userId", "organizationId"], value)));
      setPrincipal(principal);
    } else {
      setPrincipal("");
    }
  };

  const handlePrincipalInputChange = (_event, value) => {
    setPrincipalInput(value);
    handleUpdateFilter(value);
  };

  const handlePrincipalBlur = () =>
    setError(set(lensProp("principal"), isBlank(principal)));
  //-- Manage Principal End --//

  const handleRolesChange = (_event, roles) => {
    setRoles(roles);
  };

  const handlePermissionChange = permission => event => {
    const checked = event.target.checked;
    if (checked) {
      setPermissions(pipe(append(permission), uniq));
    } else {
      setPermissions(without([permission]));
    }
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <Dialog fullWidth open={open}>
      {loading && <LoadingIndicator transparent />}
      <DialogTitle>Manage Access</DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={4}>
          <Grid item>
            <Typography variant="h5">Add Organization or User</Typography>
            <Autocomplete
              id="principals"
              ListboxProps={{ ref: rootRef }}
              disabled={notBlank(mediaAccess.principal)}
              defaultValue={
                notBlank(mediaAccess.principal) ? mediaAccess.principal : ""
              }
              inputValue={principalInput}
              options={results.data}
              loading={results.isLoading}
              filterOptions={options => {
                if (results.hasMore) {
                  options.push({ id: "sentry", label: "" });
                }

                return options;
              }}
              onChange={handlePrincipalChange}
              onInputChange={handlePrincipalInputChange}
              onBlur={handlePrincipalBlur}
              getOptionLabel={opt => {
                // If editing, option is already principal string
                if (equals(type(opt), "String")) {
                  return opt;
                }

                // If new permission, grab principal from user or org
                if (notBlank(opt)) {
                  return opt.organizationName
                    ? opt.organizationName
                    : opt.username;
                }
              }}
              isOptionEqualToValue={(opt, val) => {
                if (notBlank(val)) {
                  return has("organizationId", opt)
                    ? equals(opt.organizationId, val.organizationId)
                    : equals(opt.userId, val.userId);
                }
              }}
              renderOption={(props, option) => {
                if (option.id === "sentry") {
                  return renderSentry();
                }

                const template = {
                  name: option.organizationName || option.username,
                  id: option.organizationId || option.userId,
                  active: option.active,
                };

                return (
                  <li {...props}>
                    <Box>
                      <Box fontSize="1.4rem">
                        <Typography
                          color={not(template.active) ? "error" : "inherit"}
                        >
                          {template.name}
                        </Typography>
                      </Box>
                      <Box fontSize="1.2rem">
                        <Typography
                          color={not(template.active) ? "error" : "inherit"}
                        >
                          {`(${template.id})`}
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                );
              }}
              renderInput={params => {
                params.inputProps["aria-autocomplete"] = "none";
                params.inputProps["id"] = null;
                return (
                  <TextField
                    {...params}
                    required
                    variant="outlined"
                    type="text"
                    label="Users and Organizations"
                    name={"org-or-user-" + Math.random()}
                    fullWidth
                  />
                );
              }}
            />
          </Grid>

          <Grid item>
            <Typography variant="h5">Select Roles</Typography>
            <Autocomplete
              id="roles"
              multiple={!isEdit}
              value={roles || []}
              options={rolesResults.isSuccess ? rolesResults.data : []}
              loading={rolesResults.isLoading}
              getOptionLabel={option => option.roleName}
              onChange={handleRolesChange}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    color="primary"
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.roleName}
                </li>
              )}
              renderInput={params => {
                params.inputProps["aria-autocomplete"] = "none";
                params.inputProps["id"] = null;
                return (
                  <TextField
                    {...params}
                    type="text"
                    variant="outlined"
                    label="Roles"
                    name={"roles-" + Math.random()}
                    fullWidth
                  />
                );
              }}
            />
            {error.principal && (
              <FormHelperText error>
                An organization or user must be selected
              </FormHelperText>
            )}
          </Grid>

          <Grid container item direction="column">
            <Grid item>
              <Typography variant="h5">Select Permissions</Typography>
            </Grid>
            <FormControl component="fieldset" required>
              <FormLabel
                classes={{
                  root: classes.formLabel,
                }}
              >
                To assign ownership to an organization or user, ensure{" "}
                <strong>View</strong> and <strong>Update</strong> are both
                selected
              </FormLabel>
              <FormGroup row>
                {permissionsMap.map(permission => (
                  <FormControlLabel
                    key={`permission-option-${permission.value}`}
                    label={permission.label}
                    control={
                      <Checkbox
                        color="primary"
                        onChange={handlePermissionChange(permission.value)}
                        checked={
                          notBlank(permissions) &&
                          includes(permission.value, permissions)
                        }
                      />
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageMediaAccessDialog;
